/**
 * PAPPO — PunishPanel (console /staff, dentro do detalhe da denúncia)
 * A equipe aplica uma punição sobre o adulto denunciado e vê/revoga o histórico.
 *
 * Escalonamento (espelha o backend, que é a autoridade): Gerente aplica
 * advertência/silenciar/suspender; **banir** só Master/Dev. Revogar segue a
 * mesma regra (ban exige Master/Dev).
 */
import { useCallback, useEffect, useState } from 'react';
import { Gavel, ShieldOff, Clock, Ban, AlertTriangle, VolumeX, Undo2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  punishUser,
  listPunishments,
  liftPunishment,
  PUNISH_LABEL,
  type PunishmentType,
  type PunishmentItem,
  type ApplyPunishment,
} from '../../services/moderation';
import styles from './PunishPanel.module.css';

interface PunishPanelProps {
  targetId: number;
  targetName: string;
  reportId?: number;
}

const DURACOES: { value: number; label: string }[] = [
  { value: 1, label: '1 hora' },
  { value: 24, label: '24 horas' },
  { value: 168, label: '7 dias' },
  { value: 720, label: '30 dias' },
  { value: 0, label: 'Sem prazo' },
];

const TYPE_ICON: Record<PunishmentType, React.ReactNode> = {
  warning: <AlertTriangle size={14} />,
  mute: <VolumeX size={14} />,
  suspend: <Clock size={14} />,
  ban: <Ban size={14} />,
};

function fmt(s: string | null): string {
  if (!s) return '';
  return new Date(s).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PunishPanel({ targetId, targetName, reportId }: PunishPanelProps) {
  const { user } = useAuth();
  const canBan = user?.staff_role === 'master' || user?.staff_role === 'dev';

  const [type, setType] = useState<PunishmentType>('warning');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<number>(24);
  const [hist, setHist] = useState<PunishmentItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const tipos: PunishmentType[] = canBan
    ? ['warning', 'mute', 'suspend', 'ban']
    : ['warning', 'mute', 'suspend'];

  const load = useCallback(async () => {
    const r = await listPunishments(targetId);
    if (r.success && r.data) setHist(r.data.punishments);
  }, [targetId]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    const body: ApplyPunishment = { type, reason: reason.trim() };
    if (reportId) body.report_id = reportId;
    if ((type === 'mute' || type === 'suspend') && duration > 0) {
      body.duration_horas = duration;
    }
    const r = await punishUser(targetId, body);
    setBusy(false);
    if (r.success) {
      setMsg({ ok: true, text: `${PUNISH_LABEL[type]} aplicada a ${targetName}.` });
      setReason('');
      await load();
    } else {
      setMsg({ ok: false, text: r.error || 'Não foi possível aplicar.' });
    }
  }, [busy, type, reason, duration, reportId, targetId, targetName, load]);

  const lift = useCallback(
    async (pid: number) => {
      const r = await liftPunishment(pid);
      if (r.success) {
        setMsg({ ok: true, text: 'Punição revogada.' });
        await load();
      } else {
        setMsg({ ok: false, text: r.error || 'Não foi possível revogar.' });
      }
    },
    [load]
  );

  const withDuration = type === 'mute' || type === 'suspend';
  const ativos = hist.filter((p) => p.em_efeito);

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <Gavel size={16} />
        <span>Ação de moderação</span>
      </div>

      {/* Punições em efeito */}
      {ativos.length > 0 && (
        <div className={styles.activeBox}>
          <div className={styles.activeTitle}>
            <ShieldOff size={13} /> Em efeito
          </div>
          {ativos.map((p) => (
            <div key={p.id} className={styles.activeRow}>
              <span className={`${styles.typeBadge} ${styles[p.type]}`}>
                {TYPE_ICON[p.type]} {PUNISH_LABEL[p.type]}
              </span>
              <span className={styles.activeMeta}>
                {p.expires_at ? `até ${fmt(p.expires_at)}` : 'permanente'}
              </span>
              {(p.type !== 'ban' || canBan) && (
                <button
                  type="button"
                  className={styles.liftBtn}
                  onClick={() => lift(p.id)}
                  title="Revogar"
                >
                  <Undo2 size={13} /> Revogar
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Aplicar */}
      <div className={styles.form}>
        <div className={styles.controls}>
          <select
            className={styles.select}
            value={type}
            onChange={(e) => setType(e.target.value as PunishmentType)}
          >
            {tipos.map((t) => (
              <option key={t} value={t}>
                {PUNISH_LABEL[t]}
              </option>
            ))}
          </select>
          {withDuration && (
            <select
              className={styles.select}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {DURACOES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <textarea
          className={styles.reason}
          rows={2}
          maxLength={2000}
          placeholder="Motivo / justificativa (fica no histórico e na advertência)."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {!canBan && (
          <span className={styles.hint}>
            Banir é restrito a Master/Dev.
          </span>
        )}
        <button
          type="button"
          className={styles.applyBtn}
          onClick={apply}
          disabled={busy}
        >
          <Gavel size={15} /> {busy ? 'Aplicando…' : `Aplicar ${PUNISH_LABEL[type]}`}
        </button>
        {msg && (
          <div className={`${styles.msg} ${msg.ok ? styles.ok : styles.err}`}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Histórico */}
      {hist.length > 0 && (
        <details className={styles.histBox}>
          <summary className={styles.histSummary}>
            Histórico ({hist.length})
          </summary>
          <ul className={styles.histList}>
            {hist.map((p) => (
              <li key={p.id} className={styles.histRow}>
                <span className={`${styles.typeBadge} ${styles[p.type]}`}>
                  {TYPE_ICON[p.type]} {PUNISH_LABEL[p.type]}
                </span>
                <div className={styles.histInfo}>
                  <span className={styles.histReason}>{p.reason || '—'}</span>
                  <span className={styles.histMeta}>
                    {p.issued_name ? `por ${p.issued_name} · ` : ''}
                    {fmt(p.created_at)}
                    {p.em_efeito
                      ? ' · em efeito'
                      : p.lifted_at
                      ? ` · revogada ${p.lifted_name ? `por ${p.lifted_name}` : ''}`
                      : p.type === 'warning' && p.acknowledged
                      ? ' · vista'
                      : p.expires_at && !p.active
                      ? ' · encerrada'
                      : ' · expirada'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
